import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { ChevronDown, ChevronUp, Calendar, ExternalLink, FileText } from 'lucide-react';
import './App.css';

const AssembleeLLM = () => {
  const [expandedCard, setExpandedCard] = useState(null);
  const [seances, setSeances] = useState([]);

  // Charger le JSON depuis public/seances/summary.json
  useEffect(() => {
    fetch('https://assemblee.etchegoyen.org/api/records')
      .then((res) => res.json())
      .then((data) => setSeances(data))
      .catch((err) => console.error("Erreur lors du chargement des séances :", err));
  }, []);

  const toggleCard = (url) => {
    setExpandedCard(expandedCard === url ? null : url);
  };

  const renderMarkdownTable = (markdown) => {
    const lines = markdown.trim().split('\n');
    const headers = lines[0].split('|').filter(cell => cell.trim());
    const rows = lines.slice(2).map(line => 
      line.split('|').filter(cell => cell.trim())
    );

    return (
      <div className="overflow-x-auto mt-6">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-blue-50">
              {headers.map((header, i) => (
                <th key={i} className="border border-blue-200 px-4 py-3 text-left font-semibold text-blue-900">
                  {header.trim()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="hover:bg-gray-50 transition-colors">
                {row.map((cell, j) => (
                  <td key={j} className="border border-gray-200 px-4 py-3 text-gray-700">

                    {
			    (
			    <ReactMarkdown>{cell.trim()}</ReactMarkdown>
                      )  
		    }
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-red-50">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-10 border-b-4 border-blue-600">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-center space-x-4">
            <div className="flex space-x-1">
              <div className="w-2 h-12 bg-blue-600 rounded"></div>
              <div className="w-2 h-12 bg-white border border-gray-300 rounded"></div>
              <div className="w-2 h-12 bg-red-600 rounded"></div>
            </div>
            <h1 className="text-4xl font-bold text-gray-800">
              Assemblée <span className="text-blue-600">LLM</span>
            </h1>
          </div>
          <p className="text-center text-gray-600 mt-2 text-sm">
            Résumés intelligents des séances de l'Assemblée Nationale
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="space-y-8">
          {seances.map((seance) => (
            <div
              key={seance.url}
              className={`bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 border-2 ${
                expandedCard === seance.url 
                  ? 'border-blue-600 shadow-2xl' 
                  : 'border-gray-200 hover:border-blue-400 hover:shadow-xl'
              }`}
            >
              {/* Card Header - Clickable */}
              <div
                onClick={() => toggleCard(seance.url)}
                className="cursor-pointer p-8 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <Calendar className="w-5 h-5 text-blue-600" />
                      <span className="text-sm font-medium text-blue-600">
                        {new Date(seance.date).toLocaleDateString('fr-FR', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
		    {seance.url && (
			    <a href={seance.url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-600">
			    <ExternalLink className="w-5 h-5 inline-block ml-1"/>
			    </a>
		    )}
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">
                      {seance.titre}
                    </h2>
		    <div className="prose prose-blue">
                    <ReactMarkdown className="text-gray-600 leading-relaxed">
                      {seance.sommaire}
                    </ReactMarkdown>
		    </div>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    {expandedCard === seance.url ? (
                      <ChevronUp className="w-8 h-8 text-blue-600" />
                    ) : (
                      <ChevronDown className="w-8 h-8 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              {expandedCard === seance.url && (
                <div className="border-t-2 border-gray-100 bg-gray-50 p-8 animate-in fade-in duration-300">
                  <div className="flex items-center space-x-2 mb-4">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <h3 className="text-xl font-semibold text-gray-800">
                      Détails de la séance
                    </h3>
                  </div>
                  {renderMarkdownTable(seance.tableau)}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer Info */}
        <div className="mt-16 text-center text-gray-500 text-sm">
          <p>💡 Cliquez sur une carte pour voir les détails complets</p>
          <p className="mt-2">
            Données extraites et analysées automatiquement depuis{' '}
            <a 
              href="https://www.assemblee-nationale.fr" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              assemblee-nationale.fr
            </a>
          </p>
        </div>
      </main>
    </div>
  );
};

export default AssembleeLLM;

